import { TFunction } from 'i18next'
import { AgentType } from '../../agent-type'

export type CampaignSettingsPanelKey =
    | 'general'
    | 'content'
    | 'whatsapp'
    | 'candidate-sources'
    | 'clients'
    | 'missed-candidates'

export type CampaignSettingsPanelConfig = {
    title: string
    description: string
    canSave: boolean
    // The navigation item that is active while this panel is open.
    navigationKey: CampaignSettingsPanelKey
}

export type CampaignSettingsNavigationItem = {
    key: string
    label: string
}

type NavigationAccess = {
    whatsapp: boolean
    clients: boolean
}

const panelKeys: CampaignSettingsPanelKey[] = [
    'general',
    'content',
    'whatsapp',
    'candidate-sources',
    'clients',
    'missed-candidates',
]

export function campaignSettingsPath(campaignId: string, key: CampaignSettingsPanelKey) {
    return key === 'general' ? `/campaigns/${campaignId}/settings` : `/campaigns/${campaignId}/settings/${key}`
}

export function campaignSettingsPanelKey(pathname: string, campaignId: string): CampaignSettingsPanelKey {
    const segment = pathname
        .replace(`/campaigns/${campaignId}/settings`, '')
        .replace(/^\/|\/$/g, '') as CampaignSettingsPanelKey

    return panelKeys.includes(segment) ? segment : 'general'
}

function conversationDescription(t: TFunction, type: AgentType) {
    const descriptions: Record<AgentType, string> = {
        interview: t('Set the job posting, questions, and instructions for candidate interviews.'),
        sales: t('Set the instructions for Evaa sales conversations.'),
        custom: t('Set the goal, tone and approach for your custom conversation.'),
    }

    return descriptions[type]
}

export function campaignSettingsPanels(
    t: TFunction,
    type: AgentType
): Record<CampaignSettingsPanelKey, CampaignSettingsPanelConfig> {
    return {
        general: {
            title: t('General'),
            description: t('Manage this campaign’s details, limits, and notifications.'),
            canSave: true,
            navigationKey: 'general',
        },
        content: {
            title: t('Conversation'),
            description: conversationDescription(t, type),
            canSave: true,
            navigationKey: 'content',
        },
        whatsapp: {
            title: t('WhatsApp'),
            description: t('Choose the number candidates hear from and the templates sent at each stage.'),
            canSave: true,
            navigationKey: 'whatsapp',
        },
        'candidate-sources': {
            title: t('Candidate sources'),
            description: t('Collect candidates through your public form and Facebook lead form.'),
            canSave: false,
            navigationKey: 'candidate-sources',
        },
        clients: {
            title: t('Client access'),
            description: t('Give clients read access so they can follow this campaign.'),
            canSave: false,
            navigationKey: 'clients',
        },
        'missed-candidates': {
            title: t('Missed candidates'),
            description: t('Review inbound candidates that could not be created and retry them.'),
            canSave: false,
            navigationKey: 'candidate-sources',
        },
    }
}

export function campaignSettingsNavigation(
    t: TFunction,
    campaignId: string,
    access: NavigationAccess
): CampaignSettingsNavigationItem[] {
    const items: { key: CampaignSettingsPanelKey; label: string; visible: boolean }[] = [
        { key: 'general', label: t('General'), visible: true },
        { key: 'content', label: t('Conversation'), visible: true },
        { key: 'whatsapp', label: t('WhatsApp'), visible: access.whatsapp },
        { key: 'candidate-sources', label: t('Candidate sources'), visible: true },
        { key: 'clients', label: t('Client access'), visible: access.clients },
    ]

    return items
        .filter((item) => item.visible)
        .map((item) => ({ key: campaignSettingsPath(campaignId, item.key), label: item.label }))
}
